import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useFacilities } from "@/hooks/useFacilities";
import { matchesFacilityPrefix } from "@/lib/document-folder-matching";

export interface DropboxFolder {
  name: string;
  path: string;
}

export interface FolderValidationResult {
  valid: boolean;
  exists: boolean;
  path: string;
  normalizedPath: string;
  isFolder: boolean;
  error?: string;
  errorCode?: string;
  diagnostics?: {
    attempts: number;
    timingMs: number;
    freshScanPerformed: boolean;
    hierarchy?: {
      deepestExisting: string | null;
      firstMissing: string | null;
      segments: { name: string; exists: boolean }[];
    };
    freshScan?: {
      parentPath: string;
      foldersFound: number;
      targetFound: boolean;
      availableFolders: string[];
    };
  };
}

/**
 * Fetch a facility's client folders from Dropbox (shared root /GWH Clients,
 * filtered to that facility's folder-name prefix, e.g. "GWH - *" or "RP - *").
 *
 * The raw folder listing is identical for every facility -- only the prefix
 * filter below differs -- so this shares one cached request across all
 * facilities via a facility-agnostic queryKey + `select`, instead of each
 * facility triggering its own live explore-dropbox call.
 */
export function useDropboxClientFolders(facilityId: string | undefined) {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { data: facilities } = useFacilities();
  const folderPrefix = facilities?.find((f) => f.id === facilityId)?.dropbox_folder_prefix?.trim() || null;

  return useQuery({
    queryKey: ["dropbox-client-folders"],
    queryFn: async (): Promise<string[]> => {
      const { data, error } = await supabase.functions.invoke('explore-dropbox', {
        body: { path: '/GWH Clients', maxDepth: 1 },
      });

      if (error) throw error;
      return data?.structure?.folders ?? [];
    },
    select: (allFolderNames): DropboxFolder[] => {
      if (!folderPrefix) return [];
      return allFolderNames
        .filter((name) => matchesFacilityPrefix(name, folderPrefix))
        .map((name) => ({ name, path: `/GWH Clients/${name}` }))
        .sort((a, b) => a.name.localeCompare(b.name));
    },
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    enabled: isAuthenticated && !authLoading && !!facilityId && !!folderPrefix, // Wait for auth + facility to be ready
  });
}

/**
 * Validate a Dropbox folder path exists
 * Uses enterprise-grade validation with retry logic and hierarchical checks
 */
export function useValidateDropboxFolder(folderPath: string | null, options?: { quick?: boolean }) {
  return useQuery({
    queryKey: ["dropbox-folder-validation", folderPath, options?.quick],
    queryFn: async (): Promise<FolderValidationResult> => {
      if (!folderPath) {
        return {
          valid: false,
          exists: false,
          path: "",
          normalizedPath: "",
          isFolder: false,
          error: "No path provided",
        };
      }

      const { data, error } = await supabase.functions.invoke('validate-dropbox-folder', {
        body: {
          path: folderPath,
          quick: options?.quick ?? false,
          includeDiagnostics: true,
        },
      });

      if (error) {
        return {
          valid: false,
          exists: false,
          path: folderPath,
          normalizedPath: folderPath,
          isFolder: false,
          error: error.message,
        };
      }

      return data as FolderValidationResult;
    },
    enabled: !!folderPath,
    staleTime: 1000 * 60 * 2, // Cache for 2 minutes
    retry: false,
  });
}

/**
 * Hook to manually trigger folder validation
 * Returns a function that can be called to validate any path
 */
export function useValidateFolderMutation() {
  const queryClient = useQueryClient();

  const validateFolder = async (folderPath: string, quick = false): Promise<FolderValidationResult> => {
    const { data, error } = await supabase.functions.invoke('validate-dropbox-folder', {
      body: {
        path: folderPath,
        quick,
        includeDiagnostics: true,
      },
    });

    if (error) {
      return {
        valid: false,
        exists: false,
        path: folderPath,
        normalizedPath: folderPath,
        isFolder: false,
        error: error.message,
      };
    }

    // Cache the result
    queryClient.setQueryData(
      ["dropbox-folder-validation", folderPath, quick],
      data
    );

    return data as FolderValidationResult;
  };

  return { validateFolder };
}

/**
 * Invalidate cached folder data to force refresh
 */
export function useInvalidateDropboxCache() {
  const queryClient = useQueryClient();

  return {
    invalidateClientFolders: () => {
      queryClient.invalidateQueries({ queryKey: ["dropbox-client-folders"], exact: true });
    },
    invalidateFolderValidation: (path?: string) => {
      if (path) {
        queryClient.invalidateQueries({ queryKey: ["dropbox-folder-validation", path] });
      } else {
        queryClient.invalidateQueries({ queryKey: ["dropbox-folder-validation"] });
      }
    },
    invalidateAll: () => {
      queryClient.invalidateQueries({ queryKey: ["dropbox-client-folders"], exact: true });
      queryClient.invalidateQueries({ queryKey: ["dropbox-folder-validation"] });
    },
  };
}
